import React, { useState, memo } from 'react';
import { ShieldCheckIcon, CalendarCheckIcon, HomeIcon, BrainIcon, HeartHandshakeIcon, ArrowRightIcon, StarIcon, ClockIcon, UsersIcon, ChevronRightIcon, MonitorIcon, HeadsetIcon, FileTextIcon, SearchIcon } from 'lucide-react';
export function ServicesSection() {
  const [activeTab, setActiveTab] = useState<'hire' | 'care' | 'virtual'>('hire');
  return <section className="w-full bg-white py-16 lg:py-24 border-b border-gray-100" aria-label="Services" data-id="element-448">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-449">
        {/* Section header */}
        <div className="text-center mb-10" data-id="element-450">
          <div className="inline-flex items-center gap-2 bg-soft-teal text-accent-teal-dark text-xs font-semibold px-3 py-1.5 rounded-full mb-4" data-id="element-451">
            <ShieldCheckIcon className="w-3.5 h-3.5" data-id="element-452" />
            Nurse-Led & Clinically Overseen
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-text-dark tracking-tight mb-3" data-id="element-453">
            Healthcare Staffing &{' '}
            <span className="text-medical-blue" data-id="element-454">Care Solutions</span>
          </h2>
          <p className="text-text-muted text-lg max-w-2xl mx-auto" data-id="element-455">
            Every professional on our platform is vetted and managed by a
            Registered Nurse and former PSW — ensuring clinical standards you
            can trust.
          </p>
        </div>

        {/* Tab switcher */}
        <div className="flex justify-center mb-10" data-id="element-456">
          <div className="inline-flex bg-gray-100 rounded-2xl p-1.5 gap-1" data-id="element-457">
            <button onClick={() => setActiveTab('hire')} className={`px-6 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 ${activeTab === 'hire' ? 'bg-medical-blue text-white shadow-sm' : 'text-text-muted hover:text-text-dark'}`} aria-pressed={activeTab === 'hire'} data-id="element-458">
              Hire Now
            </button>
            <button onClick={() => setActiveTab('care')} className={`px-6 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 ${activeTab === 'care' ? 'bg-medical-blue text-white shadow-sm' : 'text-text-muted hover:text-text-dark'}`} aria-pressed={activeTab === 'care'} data-id="element-459">
              Care Solutions
            </button>
            <button onClick={() => setActiveTab('virtual')} className={`px-6 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 ${activeTab === 'virtual' ? 'bg-medical-blue text-white shadow-sm' : 'text-text-muted hover:text-text-dark'}`} aria-pressed={activeTab === 'virtual'} data-id="element-460">
              Virtual Assistant services
            </button>
          </div>
        </div>

        {/* Hire Now Grid */}
        {activeTab === 'hire' && <div data-id="element-461">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-462">
              {/* RN Card */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-float overflow-hidden group" data-id="element-463">
                {/* Card top bar */}
                <div className="relative h-48 overflow-hidden" data-id="element-464">
                  <img src="https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=800&auto=format&fit=crop" alt="Registered Nurse" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-465" />
                  <div className="absolute inset-0 bg-medical-blue/60 mix-blend-multiply" data-id="element-466" />
                  <div className="absolute inset-0 p-6 flex flex-col justify-between" data-id="element-467">
                    <div className="flex items-start justify-between" data-id="element-468">
                      <div className="w-12 h-12 bg-white/20 backdrop-blur-md rounded-xl flex items-center justify-center border border-white/30" data-id="element-469">
                        <span className="text-white font-extrabold text-lg" data-id="element-470">
                          RN
                        </span>
                      </div>
                      <span className="flex items-center gap-1 bg-white/20 backdrop-blur-md text-white text-[10px] font-bold px-2.5 py-1 rounded-full border border-white/30" data-id="element-471">
                        <ShieldCheckIcon className="w-3 h-3" data-id="element-472" />
                        Clinical Oversight
                      </span>
                    </div>
                    <div data-id="element-473">
                      <h3 className="text-white font-extrabold text-xl leading-tight" data-id="element-474">
                        Registered Nurse
                      </h3>
                      <p className="text-white/80 text-xs mt-1 font-medium tracking-wide uppercase" data-id="element-475">
                        Full scope of practice
                      </p>
                    </div>
                  </div>
                </div>

                {/* Card body */}
                <div className="px-6 py-5" data-id="element-476">
                  <div className="space-y-3 mb-5" data-id="element-477">
                    <div className="flex items-center gap-2 text-sm text-text-muted" data-id="element-478">
                      <StarIcon className="w-4 h-4 text-yellow-400 fill-yellow-400 flex-shrink-0" data-id="element-479" />
                      <span data-id="element-480">4.9★ avg. rating across 1,200+ shifts</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-text-muted" data-id="element-481">
                      <ClockIcon className="w-4 h-4 text-accent-teal flex-shrink-0" data-id="element-482" />
                      <span data-id="element-483">Day, evening & overnight shifts</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-text-muted" data-id="element-484">
                      <ShieldCheckIcon className="w-4 h-4 text-accent-teal flex-shrink-0" data-id="element-485" />
                      <span data-id="element-486">CNO registered & credential-verified</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-text-muted" data-id="element-487">
                      <UsersIcon className="w-4 h-4 text-accent-teal flex-shrink-0" data-id="element-488" />
                      <span data-id="element-489">Vetted by our RN clinical team</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-4 py-3 border-t border-gray-100" data-id="element-490">
                    <div data-id="element-491">
                      <div className="text-xs text-text-muted font-medium" data-id="element-492">
                        Starting from
                      </div>
                      <div className="text-medical-blue font-extrabold text-xl" data-id="element-493">
                        $42
                        <span className="text-sm font-semibold text-text-muted" data-id="element-494">
                          /hr
                        </span>
                      </div>
                    </div>
                    <div className="text-right" data-id="element-495">
                      <div className="text-xs text-text-muted font-medium" data-id="element-496">
                        Available
                      </div>
                      <div className="flex items-center gap-1 text-accent-teal font-bold text-sm" data-id="element-497">
                        <span className="w-1.5 h-1.5 bg-accent-teal rounded-full live-dot" data-id="element-498" />
                        Now
                      </div>
                    </div>
                  </div>

                  <button className="w-full bg-medical-blue hover:bg-medical-blue-dark text-white font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2 text-sm" data-id="element-499">
                    <CalendarCheckIcon className="w-4 h-4" data-id="element-500" />
                    View Availability
                  </button>
                </div>
              </div>

              {/* RPN Card */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-float overflow-hidden group" data-id="element-501">
                <div className="relative h-48 overflow-hidden" data-id="element-502">
                  <img src="https://images.unsplash.com/photo-1631815589968-fdb09a223b1e?w=800&auto=format&fit=crop" alt="Registered Practical Nurse" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-503" />
                  <div className="absolute inset-0 bg-[#6366f1]/60 mix-blend-multiply" data-id="element-504" />
                  <div className="absolute inset-0 p-6 flex flex-col justify-between" data-id="element-505">
                    <div className="flex items-start justify-between" data-id="element-506">
                      <div className="w-12 h-12 bg-white/20 backdrop-blur-md rounded-xl flex items-center justify-center border border-white/30" data-id="element-507">
                        <span className="text-white font-extrabold text-lg" data-id="element-508">
                          RPN
                        </span>
                      </div>
                      <span className="flex items-center gap-1 bg-white/20 backdrop-blur-md text-white text-[10px] font-bold px-2.5 py-1 rounded-full border border-white/30" data-id="element-509">
                        <ShieldCheckIcon className="w-3 h-3" data-id="element-510" />
                        Clinical Oversight
                      </span>
                    </div>
                    <div data-id="element-511">
                      <h3 className="text-white font-extrabold text-xl leading-tight" data-id="element-512">
                        Registered Practical Nurse
                      </h3>
                      <p className="text-white/80 text-xs mt-1 font-medium tracking-wide uppercase" data-id="element-513">
                        Skilled clinical support
                      </p>
                    </div>
                  </div>
                </div>

                <div className="px-6 py-5" data-id="element-514">
                  <div className="space-y-3 mb-5" data-id="element-515">
                    <div className="flex items-center gap-2 text-sm text-text-muted" data-id="element-516">
                      <StarIcon className="w-4 h-4 text-yellow-400 fill-yellow-400 flex-shrink-0" data-id="element-517" />
                      <span data-id="element-518">4.8★ avg. rating across 890+ shifts</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-text-muted" data-id="element-519">
                      <ClockIcon className="w-4 h-4 text-accent-teal flex-shrink-0" data-id="element-520" />
                      <span data-id="element-521">Flexible scheduling, all shifts</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-text-muted" data-id="element-522">
                      <ShieldCheckIcon className="w-4 h-4 text-accent-teal flex-shrink-0" data-id="element-523" />
                      <span data-id="element-524">CNO/CARNA registered & verified</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-text-muted" data-id="element-525">
                      <UsersIcon className="w-4 h-4 text-accent-teal flex-shrink-0" data-id="element-526" />
                      <span data-id="element-527">Vetted by our RN clinical team</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-4 py-3 border-t border-gray-100" data-id="element-528">
                    <div data-id="element-529">
                      <div className="text-xs text-text-muted font-medium" data-id="element-530">
                        Starting from
                      </div>
                      <div className="text-[#6366f1] font-extrabold text-xl" data-id="element-531">
                        $36
                        <span className="text-sm font-semibold text-text-muted" data-id="element-532">
                          /hr
                        </span>
                      </div>
                    </div>
                    <div className="text-right" data-id="element-533">
                      <div className="text-xs text-text-muted font-medium" data-id="element-534">
                        Available
                      </div>
                      <div className="flex items-center gap-1 text-accent-teal font-bold text-sm" data-id="element-535">
                        <span className="w-1.5 h-1.5 bg-accent-teal rounded-full live-dot" data-id="element-536" />
                        Now
                      </div>
                    </div>
                  </div>

                  <button className="w-full bg-[#6366f1] hover:bg-[#4f46e5] text-white font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2 text-sm" data-id="element-537">
                    <CalendarCheckIcon className="w-4 h-4" data-id="element-538" />
                    View Availability
                  </button>
                </div>
              </div>

              {/* PSW Card */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-float overflow-hidden group" data-id="element-539">
                <div className="relative h-48 overflow-hidden" data-id="element-540">
                  <img src="https://images.unsplash.com/photo-1576765608535-5f04d1e3f289?w=800&auto=format&fit=crop" alt="Personal Support Worker" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-541" />
                  <div className="absolute inset-0 bg-accent-teal/60 mix-blend-multiply" data-id="element-542" />
                  <div className="absolute inset-0 p-6 flex flex-col justify-between" data-id="element-543">
                    <div className="flex items-start justify-between" data-id="element-544">
                      <div className="w-12 h-12 bg-white/20 backdrop-blur-md rounded-xl flex items-center justify-center border border-white/30" data-id="element-545">
                        <span className="text-white font-extrabold text-lg" data-id="element-546">
                          PSW
                        </span>
                      </div>
                      <span className="flex items-center gap-1 bg-white/20 backdrop-blur-md text-white text-[10px] font-bold px-2.5 py-1 rounded-full border border-white/30" data-id="element-547">
                        <ShieldCheckIcon className="w-3 h-3" data-id="element-548" />
                        Clinical Oversight
                      </span>
                    </div>
                    <div data-id="element-549">
                      <h3 className="text-white font-extrabold text-xl leading-tight" data-id="element-550">
                        Personal Support Worker
                      </h3>
                      <p className="text-white/80 text-xs mt-1 font-medium tracking-wide uppercase" data-id="element-551">
                        Compassionate daily care
                      </p>
                    </div>
                  </div>
                </div>

                <div className="px-6 py-5" data-id="element-552">
                  <div className="space-y-3 mb-5" data-id="element-553">
                    <div className="flex items-center gap-2 text-sm text-text-muted" data-id="element-554">
                      <StarIcon className="w-4 h-4 text-yellow-400 fill-yellow-400 flex-shrink-0" data-id="element-555" />
                      <span data-id="element-556">4.7★ avg. rating across 2,100+ shifts</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-text-muted" data-id="element-557">
                      <ClockIcon className="w-4 h-4 text-accent-teal flex-shrink-0" data-id="element-558" />
                      <span data-id="element-559">24/7 availability, all regions</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-text-muted" data-id="element-560">
                      <ShieldCheckIcon className="w-4 h-4 text-accent-teal flex-shrink-0" data-id="element-561" />
                      <span data-id="element-562">Background-checked & insured</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-text-muted" data-id="element-563">
                      <UsersIcon className="w-4 h-4 text-accent-teal flex-shrink-0" data-id="element-564" />
                      <span data-id="element-565">Vetted by our RN & former PSW team</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-4 py-3 border-t border-gray-100" data-id="element-566">
                    <div data-id="element-567">
                      <div className="text-xs text-text-muted font-medium" data-id="element-568">
                        Starting from
                      </div>
                      <div className="text-accent-teal font-extrabold text-xl" data-id="element-569">
                        $26
                        <span className="text-sm font-semibold text-text-muted" data-id="element-570">
                          /hr
                        </span>
                      </div>
                    </div>
                    <div className="text-right" data-id="element-571">
                      <div className="text-xs text-text-muted font-medium" data-id="element-572">
                        Available
                      </div>
                      <div className="flex items-center gap-1 text-accent-teal font-bold text-sm" data-id="element-573">
                        <span className="w-1.5 h-1.5 bg-accent-teal rounded-full live-dot" data-id="element-574" />
                        Now
                      </div>
                    </div>
                  </div>

                  <button className="w-full bg-accent-teal hover:bg-accent-teal-dark text-white font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2 text-sm" data-id="element-575">
                    <CalendarCheckIcon className="w-4 h-4" data-id="element-576" />
                    View Availability
                  </button>
                </div>
              </div>
            </div>

            {/* Nurse-Led USP banner */}
            <div className="bg-soft-teal rounded-2xl px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-577">
              <div className="flex items-center gap-3" data-id="element-578">
                <div className="w-10 h-10 bg-accent-teal rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-579">
                  <ShieldCheckIcon className="w-5 h-5 text-white" data-id="element-580" />
                </div>
                <div data-id="element-581">
                  <div className="font-bold text-text-dark text-sm" data-id="element-582">
                    Clinical Oversight on Every Hire
                  </div>
                  <div className="text-text-muted text-sm" data-id="element-583">
                    All workers are screened and managed by our Registered Nurse
                    and former PSW leadership team — not HR generalists.
                  </div>
                </div>
              </div>
              <a href="#why-us" className="flex-shrink-0 flex items-center gap-1.5 text-medical-blue font-bold text-sm hover:underline" data-id="element-584">
                Learn more <ChevronRightIcon className="w-4 h-4" data-id="element-585" />
              </a>
            </div>
          </div>}

        {/* Care Solutions Menu */}
        {activeTab === 'care' && <div data-id="element-586">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-587">
              {/* In-Home Nursing */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-float overflow-hidden group cursor-pointer" data-id="element-588">
                <div className="h-40 overflow-hidden relative" data-id="element-589">
                  <img src="https://images.unsplash.com/photo-1581594693702-fbdc51b2763b?w=800&auto=format&fit=crop" alt="In-Home Nursing" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-590" />
                  <div className="absolute inset-0 bg-medical-blue/20" data-id="element-591" />
                </div>
                <div className="p-6" data-id="element-592">
                  <div className="flex items-center gap-2 mb-2" data-id="element-593">
                    <h3 className="font-extrabold text-text-dark text-lg" data-id="element-594">
                      In-Home Nursing
                    </h3>
                    <span className="flex items-center gap-1 bg-soft-teal text-accent-teal-dark text-[10px] font-bold px-2 py-0.5 rounded-full" data-id="element-595">
                      <ShieldCheckIcon className="w-2.5 h-2.5" data-id="element-596" />
                      Clinical Oversight
                    </span>
                  </div>
                  <p className="text-text-muted text-sm leading-relaxed mb-5" data-id="element-597">
                    Professional nursing care delivered in the comfort of your
                    home. Wound care, medication management, post-surgical
                    support, and chronic disease monitoring.
                  </p>
                  <ul className="space-y-2 mb-6" data-id="element-598">
                    <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-599">
                      <span className="w-1.5 h-1.5 bg-medical-blue rounded-full flex-shrink-0" data-id="element-600" />
                      Post-surgical recovery care
                    </li>
                    <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-601">
                      <span className="w-1.5 h-1.5 bg-medical-blue rounded-full flex-shrink-0" data-id="element-602" />
                      Wound care & IV therapy
                    </li>
                    <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-603">
                      <span className="w-1.5 h-1.5 bg-medical-blue rounded-full flex-shrink-0" data-id="element-604" />
                      Medication administration
                    </li>
                    <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-605">
                      <span className="w-1.5 h-1.5 bg-medical-blue rounded-full flex-shrink-0" data-id="element-606" />
                      Chronic disease management
                    </li>
                  </ul>
                  <button className="w-full border-2 border-medical-blue text-medical-blue hover:bg-medical-blue hover:text-white font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2 text-sm" data-id="element-607">
                    Learn More
                    <ArrowRightIcon className="w-4 h-4" data-id="element-608" />
                  </button>
                </div>
              </div>

              {/* Dementia Care */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-float overflow-hidden group cursor-pointer" data-id="element-609">
                <div className="h-40 overflow-hidden relative" data-id="element-610">
                  <img src="https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=800&auto=format&fit=crop" alt="Dementia Care" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-611" />
                  <div className="absolute inset-0 bg-purple-600/20" data-id="element-612" />
                </div>
                <div className="p-6" data-id="element-613">
                  <div className="flex items-center gap-2 mb-2" data-id="element-614">
                    <h3 className="font-extrabold text-text-dark text-lg" data-id="element-615">
                      Dementia Care
                    </h3>
                    <span className="flex items-center gap-1 bg-soft-teal text-accent-teal-dark text-[10px] font-bold px-2 py-0.5 rounded-full" data-id="element-616">
                      <ShieldCheckIcon className="w-2.5 h-2.5" data-id="element-617" />
                      Clinical Oversight
                    </span>
                  </div>
                  <p className="text-text-muted text-sm leading-relaxed mb-5" data-id="element-618">
                    Specialized memory care from workers trained in dementia and
                    Alzheimer's support. Person-centred approaches that preserve
                    dignity and quality of life.
                  </p>
                  <ul className="space-y-2 mb-6" data-id="element-619">
                    <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-620">
                      <span className="w-1.5 h-1.5 bg-purple-500 rounded-full flex-shrink-0" data-id="element-621" />
                      Alzheimer's & dementia support
                    </li>
                    <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-622">
                      <span className="w-1.5 h-1.5 bg-purple-500 rounded-full flex-shrink-0" data-id="element-623" />
                      Behavioural symptom management
                    </li>
                    <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-624">
                      <span className="w-1.5 h-1.5 bg-purple-500 rounded-full flex-shrink-0" data-id="element-625" />
                      Caregiver respite & relief
                    </li>
                    <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-626">
                      <span className="w-1.5 h-1.5 bg-purple-500 rounded-full flex-shrink-0" data-id="element-627" />
                      Safe wandering prevention
                    </li>
                  </ul>
                  <button className="w-full border-2 border-purple-600 text-purple-600 hover:bg-purple-600 hover:text-white font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2 text-sm" data-id="element-628">
                    Learn More
                    <ArrowRightIcon className="w-4 h-4" data-id="element-629" />
                  </button>
                </div>
              </div>

              {/* Personal Care Services */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-float overflow-hidden group cursor-pointer" data-id="element-630">
                <div className="h-40 overflow-hidden relative" data-id="element-631">
                  <img src="https://images.unsplash.com/photo-1594824476967-48c8b964273f?w=800&auto=format&fit=crop" alt="Personal Care" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-632" />
                  <div className="absolute inset-0 bg-rose-500/20" data-id="element-633" />
                </div>
                <div className="p-6" data-id="element-634">
                  <div className="flex items-center gap-2 mb-2" data-id="element-635">
                    <h3 className="font-extrabold text-text-dark text-lg" data-id="element-636">
                      Personal Care
                    </h3>
                    <span className="flex items-center gap-1 bg-soft-teal text-accent-teal-dark text-[10px] font-bold px-2 py-0.5 rounded-full" data-id="element-637">
                      <ShieldCheckIcon className="w-2.5 h-2.5" data-id="element-638" />
                      Clinical Oversight
                    </span>
                  </div>
                  <p className="text-text-muted text-sm leading-relaxed mb-5" data-id="element-639">
                    Compassionate daily living support from vetted PSWs.
                    Bathing, grooming, mobility assistance, meal preparation,
                    and companionship — all with clinical oversight.
                  </p>
                  <ul className="space-y-2 mb-6" data-id="element-640">
                    <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-641">
                      <span className="w-1.5 h-1.5 bg-rose-400 rounded-full flex-shrink-0" data-id="element-642" />
                      Bathing & personal hygiene
                    </li>
                    <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-643">
                      <span className="w-1.5 h-1.5 bg-rose-400 rounded-full flex-shrink-0" data-id="element-644" />
                      Mobility & transfer assistance
                    </li>
                    <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-645">
                      <span className="w-1.5 h-1.5 bg-rose-400 rounded-full flex-shrink-0" data-id="element-646" />
                      Meal prep & nutrition support
                    </li>
                    <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-647">
                      <span className="w-1.5 h-1.5 bg-rose-400 rounded-full flex-shrink-0" data-id="element-648" />
                      Companionship & social engagement
                    </li>
                  </ul>
                  <button className="w-full border-2 border-rose-500 text-rose-500 hover:bg-rose-500 hover:text-white font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2 text-sm" data-id="element-649">
                    Learn More
                    <ArrowRightIcon className="w-4 h-4" data-id="element-650" />
                  </button>
                </div>
              </div>
            </div>

            {/* Care Solutions USP banner */}
            <div className="bg-soft-teal rounded-2xl px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-651">
              <div className="flex items-center gap-3" data-id="element-652">
                <div className="w-10 h-10 bg-accent-teal rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-653">
                  <ShieldCheckIcon className="w-5 h-5 text-white" data-id="element-654" />
                </div>
                <div data-id="element-655">
                  <div className="font-bold text-text-dark text-sm" data-id="element-656">
                    Every Care Plan is Nurse-Supervised
                  </div>
                  <div className="text-text-muted text-sm" data-id="element-657">
                    Our founder — a Registered Nurse and former PSW — personally
                    oversees care quality standards across all service
                    categories.
                  </div>
                </div>
              </div>
              <a href="#why-us" className="flex-shrink-0 flex items-center gap-1.5 text-medical-blue font-bold text-sm hover:underline" data-id="element-658">
                Our approach <ChevronRightIcon className="w-4 h-4" data-id="element-659" />
              </a>
            </div>
          </div>}

        {/* Virtual Assistant Menu */}
        {activeTab === 'virtual' && <div className="animate-slide-in-up" data-id="element-660">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-661">
              {/* Administrative Support */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-float overflow-hidden group cursor-pointer" data-id="element-662">
                <div className="h-40 overflow-hidden relative" data-id="element-663">
                  <img src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&auto=format&fit=crop" alt="Admin & Coordination" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-664" />
                  <div className="absolute inset-0 bg-medical-blue/20" data-id="element-665" />
                </div>
                <div className="p-6" data-id="element-666">
                  <div className="flex items-center gap-2 mb-2" data-id="element-667">
                    <h3 className="font-extrabold text-text-dark text-lg" data-id="element-668">
                      Admin & Coordination
                    </h3>
                  </div>
                  <p className="text-text-muted text-sm leading-relaxed mb-5" data-id="element-669">
                    Streamline your facility operations with virtual
                    administrative assistance. Scheduling, billing, and
                    documentation support.
                  </p>
                  <ul className="space-y-2 mb-6" data-id="element-670">
                    <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-671">
                      <span className="w-1.5 h-1.5 bg-medical-blue rounded-full flex-shrink-0" data-id="element-672" />
                      Outbound calls & patient coordination
                    </li>
                    <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-673">
                      <span className="w-1.5 h-1.5 bg-medical-blue rounded-full flex-shrink-0" data-id="element-674" />
                      Shift scheduling & coordination
                    </li>
                    <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-675">
                      <span className="w-1.5 h-1.5 bg-medical-blue rounded-full flex-shrink-0" data-id="element-676" />
                      Automated billing & invoicing
                    </li>
                  </ul>
                  <button className="w-full border-2 border-medical-blue text-medical-blue hover:bg-medical-blue hover:text-white font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2 text-sm" data-id="element-677">
                    Learn More
                    <ArrowRightIcon className="w-4 h-4" data-id="element-678" />
                  </button>
                </div>
              </div>

              {/* Recruitment Assistance */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-float overflow-hidden group cursor-pointer" data-id="element-679">
                <div className="h-40 overflow-hidden relative" data-id="element-680">
                  <img src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=800&auto=format&fit=crop" alt="Recruitment Help" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-681" />
                  <div className="absolute inset-0 bg-accent-teal/20" data-id="element-682" />
                </div>
                <div className="p-6" data-id="element-683">
                  <div className="flex items-center gap-2 mb-2" data-id="element-684">
                    <h3 className="font-extrabold text-text-dark text-lg" data-id="element-685">
                      Recruitment Help
                    </h3>
                  </div>
                  <p className="text-text-muted text-sm leading-relaxed mb-5" data-id="element-686">
                    Virtual support for your internal hiring needs. Candidate
                    screening, credential verification, and interview booking.
                  </p>
                  <ul className="space-y-2 mb-6" data-id="element-687">
                    <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-688">
                      <span className="w-1.5 h-1.5 bg-accent-teal rounded-full flex-shrink-0" data-id="element-689" />
                      Credential & license verification
                    </li>
                    <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-690">
                      <span className="w-1.5 h-1.5 bg-accent-teal rounded-full flex-shrink-0" data-id="element-691" />
                      Reference check automation
                    </li>
                    <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-692">
                      <span className="w-1.5 h-1.5 bg-accent-teal rounded-full flex-shrink-0" data-id="element-693" />
                      Onboarding coordination
                    </li>
                  </ul>
                  <button className="w-full border-2 border-accent-teal text-accent-teal hover:bg-accent-teal hover:text-white font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2 text-sm" data-id="element-694">
                    Learn More
                    <ArrowRightIcon className="w-4 h-4" data-id="element-695" />
                  </button>
                </div>
              </div>

              {/* 24/7 Virtual Desk */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-float overflow-hidden group cursor-pointer" data-id="element-696">
                <div className="h-40 overflow-hidden relative" data-id="element-697">
                  <img src="https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=800&auto=format&fit=crop" alt="24/7 Virtual Desk" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-698" />
                  <div className="absolute inset-0 bg-purple-600/20" data-id="element-699" />
                </div>
                <div className="p-6" data-id="element-700">
                  <div className="flex items-center gap-2 mb-2" data-id="element-701">
                    <h3 className="font-extrabold text-text-dark text-lg" data-id="element-702">
                      24/7 Virtual Desk
                    </h3>
                  </div>
                  <p className="text-text-muted text-sm leading-relaxed mb-5" data-id="element-703">
                    Round-the-clock virtual support for urgent staffing needs
                    and facility inquiries. Never miss a critical update.
                  </p>
                  <ul className="space-y-2 mb-6" data-id="element-704">
                    <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-705">
                      <span className="w-1.5 h-1.5 bg-purple-500 rounded-full flex-shrink-0" data-id="element-706" />
                      Urgent shift fulfillment support
                    </li>
                    <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-707">
                      <span className="w-1.5 h-1.5 bg-purple-500 rounded-full flex-shrink-0" data-id="element-708" />
                      Real-time incident reporting
                    </li>
                    <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-709">
                      <span className="w-1.5 h-1.5 bg-purple-500 rounded-full flex-shrink-0" data-id="element-710" />
                      Facility manager helpdesk
                    </li>
                  </ul>
                  <button className="w-full border-2 border-purple-600 text-purple-600 hover:bg-purple-600 hover:text-white font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2 text-sm" data-id="element-711">
                    Learn More
                    <ArrowRightIcon className="w-4 h-4" data-id="element-712" />
                  </button>
                </div>
              </div>
            </div>

            {/* Virtual Assistant USP banner */}
            <div className="bg-soft-teal rounded-2xl px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-713">
              <div className="flex items-center gap-3" data-id="element-714">
                <div className="w-10 h-10 bg-accent-teal rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-715">
                  <MonitorIcon className="w-5 h-5 text-white" data-id="element-716" />
                </div>
                <div data-id="element-717">
                  <div className="font-bold text-text-dark text-sm" data-id="element-718">
                    Optimize Your Facility Operations
                  </div>
                  <div className="text-text-muted text-sm" data-id="element-719">
                    Our virtual assistants are trained in healthcare operations
                    and compliance standards to provide expert remote support.
                  </div>
                </div>
              </div>
              <a href="#contact" className="flex-shrink-0 flex items-center gap-1.5 text-medical-blue font-bold text-sm hover:underline" data-id="element-720">
                Get started <ChevronRightIcon className="w-4 h-4" data-id="element-721" />
              </a>
            </div>
          </div>}
      </div>
    </section>;
}